import { timeOf } from './followedBrandStatus';
import { InspoSet, InspoSetImage, InspoSetLaunch, InspoSetSlot } from './types';

export const STORY_ASPECT = '9:16';

export const INSPO_SET_STATUS_LABEL: Record<InspoSet['status'], string> = {
  GENERATING: 'Generating',
  READY: 'Ready',
  SHORT: 'Short',
};

/** One variation's feed image and 9:16 twin, with the Meta copy they share. */
export interface VariationPair {
  variation: string;
  headline: string | null;
  caption: string | null;
  feed: InspoSetSlot | null;
  story: InspoSetSlot | null;
}

/** Group a set's slots by variation (A–E), feed first then its 9:16 twin. */
export function pairSlots(slots: InspoSetSlot[]): VariationPair[] {
  const byVariation = new Map<string, VariationPair>();
  for (const slot of slots) {
    const pair = byVariation.get(slot.variation) ?? {
      variation: slot.variation,
      headline: null,
      caption: null,
      feed: null,
      story: null,
    };
    if (slot.aspectRatio === STORY_ASPECT) pair.story = slot;
    else pair.feed = slot;
    pair.headline = pair.headline ?? slot.headline;
    pair.caption = pair.caption ?? slot.caption;
    byVariation.set(slot.variation, pair);
  }
  return [...byVariation.values()].sort((a, b) =>
    a.variation.localeCompare(b.variation),
  );
}

/** What to show for a slot: its accepted image, else the latest attempt. */
export function shownImage(slot: InspoSetSlot): {
  image: InspoSetImage;
  accepted: boolean;
} {
  return slot.accepted
    ? { image: slot.accepted, accepted: true }
    : { image: slot.current, accepted: false };
}

export function acceptedCount(set: InspoSet): number {
  return set.slots.filter(s => s.accepted).length;
}

export interface LaunchSummary {
  label: string;
  chip: string; // the short form for the set's header
  severity: 'success' | 'info' | 'warning' | 'error';
}

// The collector's STALE_LAUNCH_CLAIM_MINUTES (db/inspoSets.js): a LAUNCHING claim
// older than this was abandoned, so nothing will finish it.
export const STALE_LAUNCH_MINUTES = 30;

/**
 * A set's launch in one line. The ads' own outcome (from the Ad Launcher's
 * ledger) wins over the set's state: a queued batch whose ads all failed is a
 * failed launch.
 */
export function launchSummary(launch: InspoSetLaunch, now = Date.now()): LaunchSummary {
  const { total, done, failed, pending } = launch.ads;
  const where = launch.adsetName ? ` as ${launch.adsetName}` : '';
  // One Flexible ad per set since 2026-10-22, so "1 ad" is the usual count.
  const ads = (n: number) => `${n} ad${n === 1 ? '' : 's'}`;
  if (launch.state === 'FAILED') {
    return { label: 'Launch failed', chip: 'Launch failed', severity: 'error' };
  }
  const claimedAt = timeOf(launch.launchedAt);
  if (launch.state === 'LAUNCHING' && claimedAt != null && now - claimedAt > STALE_LAUNCH_MINUTES * 60_000) {
    return {
      label: 'Launch stalled: check History in the Ad Launcher',
      chip: 'Launch stalled',
      severity: 'warning',
    };
  }
  if (launch.state === 'LAUNCHING' || !total) {
    return { label: 'Launching…', chip: 'Launching…', severity: 'info' };
  }
  if (pending) {
    return {
      label: `Launching: ${done} of ${ads(total)} live${where}`,
      chip: 'Launching…',
      severity: 'info',
    };
  }
  if (failed === total) {
    return { label: 'Launch failed', chip: 'Launch failed', severity: 'error' };
  }
  if (failed) {
    return {
      label: `${done} of ${ads(total)} live${where}`,
      chip: `${done} of ${ads(total)} live`,
      severity: 'warning',
    };
  }
  return {
    label: `Launched${where}, ${ads(total)}`,
    chip: 'Launched',
    severity: 'success',
  };
}
